import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';

// Workshops.jsx — Mission-board style training catalog
const Workshops = (() => {
  const { Bolt, Star, Diamond, Heart, Arrow, Coin, Joystick, Book } = PixelIcons;

  const workshops = [
    {
      k: "w1", code: "M-01", level: 8,
      seats: 4, seatsTotal: 12, status: "open",
      icon: Joystick, accent: "var(--ragross-magenta)",
      glow: "rgba(253,20,145,0.35)",
    },
    {
      k: "w2", code: "M-02", level: 5,
      seats: 9, seatsTotal: 15, status: "open",
      icon: Star, accent: "var(--ragross-cyan)",
      glow: "rgba(0,232,203,0.35)",
    },
    {
      k: "w3", code: "M-03", level: 3,
      seats: 0, seatsTotal: 10, status: "full",
      icon: Diamond, accent: "var(--ragross-gold)",
      glow: "rgba(253,177,21,0.30)",
    },
    {
      k: "w4", code: "M-04", level: 6,
      seats: 14, seatsTotal: 20, status: "soon",
      icon: Bolt, accent: "var(--ragross-green)",
      glow: "rgba(79,253,21,0.30)",
    },
  ];

  const wsStyles = {
    section: {
      padding: "112px 0",
      background: "var(--bg-app)",
      position: "relative",
      borderTop: "1px solid var(--border-soft)",
      borderBottom: "1px solid var(--border-soft)",
    },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px" },
    head: { display: "flex", flexDirection: "column", gap: 14, maxWidth: 720, marginBottom: 56 },
    tag: {
      display: "inline-flex", alignItems: "center", gap: 12,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "11px", letterSpacing: "0.16em",
      color: "var(--ragross-magenta)",
    },
    title: { font: "var(--type-display-lg)", letterSpacing: "-0.015em", lineHeight: 1.05, color: "#fff", margin: 0 },
    lead: { font: "var(--type-body-lg)", color: "var(--fg-2)", maxWidth: 640, margin: 0 },

    grid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 18,
    },
    card: {
      display: "grid",
      gridTemplateColumns: "auto 1fr auto",
      gap: 24,
      alignItems: "stretch",
      padding: 20,
      background: "var(--ragross-purple-night)",
      border: "1px solid var(--border-soft)",
      borderLeft: "4px solid",
      borderRadius: "var(--radius-card)",
      transition: "transform 200ms var(--ease-arcade), box-shadow 200ms, border-color 200ms",
      position: "relative",
    },
    icoBox: (color) => ({
      width: 84,
      background:
        `radial-gradient(circle, ${color === "var(--ragross-magenta)" ? "rgba(253,20,145,0.25)" : color === "var(--ragross-cyan)" ? "rgba(0,232,203,0.25)" : color === "var(--ragross-gold)" ? "rgba(253,177,21,0.25)" : "rgba(79,253,21,0.25)"} 0%, transparent 70%), var(--ragross-purple-deep)`,
      border: `2px solid ${color}`,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      gap: 8,
      padding: "14px 8px",
    }),
    levelLabel: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.14em",
      color: "var(--fg-3)",
    },
    levelVal: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 16, color: "#fff",
    },
    content: { display: "flex", flexDirection: "column", gap: 10 },
    code: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 9, letterSpacing: "0.16em",
      color: "var(--ragross-gold)",
    },
    name: { font: "var(--type-h2)", color: "#fff", margin: 0 },
    sub: { font: "var(--type-body-sm)", color: "var(--fg-2)", margin: 0 },
    metaGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, auto)",
      gap: "4px 18px",
      marginTop: 6,
    },
    metaKey: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.14em",
      color: "var(--fg-3)",
    },
    metaVal: {
      fontFamily: "'VT323', monospace",
      fontSize: 17, color: "#fff",
      letterSpacing: "0.04em",
    },
    sideAction: {
      display: "flex", flexDirection: "column",
      alignItems: "stretch", justifyContent: "space-between",
      gap: 12, minWidth: 130,
    },
    statusBadge: (s) => ({
      padding: "6px 8px",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8,
      letterSpacing: "0.14em",
      textAlign: "center",
      background:
        s === "open" ? "rgba(79,253,21,0.10)" :
        s === "full" ? "rgba(253,20,145,0.10)" :
        "rgba(253,177,21,0.10)",
      color:
        s === "open" ? "var(--ragross-green)" :
        s === "full" ? "var(--ragross-magenta)" :
        "var(--ragross-gold)",
      border: `1px solid ${s === "open" ? "var(--ragross-green)" : s === "full" ? "var(--ragross-magenta)" : "var(--ragross-gold)"}`,
    }),
    seatsBar: {
      height: 10,
      background: "rgba(255,255,255,0.06)",
      border: "1px solid var(--border-soft)",
      position: "relative",
      overflow: "hidden",
    },
    seatsFill: (pct) => ({
      position: "absolute", top: 0, left: 0, bottom: 0,
      width: `${pct}%`,
      background: `repeating-linear-gradient(90deg, var(--ragross-cyan) 0 6px, transparent 6px 8px), var(--ragross-cyan)`,
    }),
    seatsKey: {
      display: "flex", justifyContent: "space-between",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.12em",
      color: "var(--fg-3)", marginBottom: 4,
    },
  };

  function MissionCard({ w }) {
    const t = useT();
    const [hover, setHover] = React.useState(false);
    const Icon = w.icon;
    const remaining = w.seats;
    const seatsPct = ((w.seatsTotal - remaining) / w.seatsTotal) * 100;
    const statusKey = w.status === "open" ? "ws.open" : w.status === "full" ? "ws.full" : "ws.soon";

    return (
      <article
        style={{
          ...wsStyles.card,
          borderLeftColor: w.accent,
          transform: hover ? "translate(-2px,-2px)" : "none",
          boxShadow: hover ? `4px 4px 0 ${w.accent}, 0 0 30px ${w.glow}` : "var(--shadow-card)",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="ws-card"
      >
        <div style={wsStyles.icoBox(w.accent)}>
          <Icon size={32}/>
          <span style={wsStyles.levelLabel}>LVL</span>
          <span style={wsStyles.levelVal}>{w.level.toString().padStart(2, "0")}</span>
        </div>

        <div style={wsStyles.content}>
          <span style={wsStyles.code}>{w.code} · MISIÓN</span>
          <h3 style={wsStyles.name}>{t(`ws.${w.k}.title`)}</h3>
          <p style={wsStyles.sub}>{t(`ws.${w.k}.sub`)}</p>
          <div style={wsStyles.metaGrid}>
            <span style={wsStyles.metaKey}>{t("ws.duration")}</span>
            <span style={wsStyles.metaKey}>{t("ws.mode")}</span>
            <span style={wsStyles.metaKey}>{t("ws.start")}</span>
            <span style={wsStyles.metaVal}>{t(`ws.${w.k}.dur`)}</span>
            <span style={wsStyles.metaVal}>{t(`ws.${w.k}.mode`)}</span>
            <span style={{ ...wsStyles.metaVal, color: "var(--ragross-cyan)" }}>{t(`ws.${w.k}.start`)}</span>
          </div>
        </div>

        <div style={wsStyles.sideAction}>
          <div style={wsStyles.statusBadge(w.status)}>{t(statusKey)}</div>
          <div>
            <div style={wsStyles.seatsKey}>
              <span>{t("ws.seats")}</span>
              <span style={{ color: w.status === "full" ? "var(--ragross-magenta)" : "var(--ragross-green)" }}>
                {(w.seatsTotal - remaining)}/{w.seatsTotal}
              </span>
            </div>
            <div style={wsStyles.seatsBar}>
              <div style={wsStyles.seatsFill(seatsPct)}></div>
            </div>
          </div>
          <button
            className={w.status === "full" ? "btn btn-ghost" : "btn btn-primary"}
            disabled={w.status === "full"}
            style={{
              padding: "10px 12px", fontSize: 9,
              justifyContent: "center",
              opacity: w.status === "full" ? 0.5 : 1,
              cursor: w.status === "full" ? "not-allowed" : "pointer",
            }}
          >
            {t("ws.enroll")}
            <Arrow size={10} color="#fff"/>
          </button>
        </div>
      </article>
    );
  }

  function Workshops() {
    const t = useT();
    return (
      <section id="workshops" style={wsStyles.section} data-screen-label="06 Talleres">
        <div style={wsStyles.container}>
          <div style={wsStyles.head}>
            <div style={wsStyles.tag}>
              <span>{t("ws.tag")}</span>
              <span style={{ width: 60, height: 1, background: "linear-gradient(90deg, var(--ragross-magenta), transparent)" }}></span>
            </div>
            <h2 style={wsStyles.title} className="section-title">
              {t("ws.title1")} <span style={{ color: "var(--ragross-cyan)" }}>{t("ws.title2")}</span> {t("ws.title3")}
            </h2>
            <p style={wsStyles.lead}>{t("ws.lead")}</p>
          </div>

          <div style={wsStyles.grid} className="responsive-grid-2">
            {workshops.map((w) => <MissionCard key={w.k} w={w}/>)}
          </div>
        </div>
      </section>
    );
  }

  return Workshops;
})();

export default Workshops;
